// StruggleReelScreen.jsx
// Real session replay — v2 pipeline via GET /api/session/{sid}/replay.
// Chess.com-style key-moment scrubber.
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';


// ─── v2 payload normalizer ──────────────────────────────────────────────────
// The v2 replay endpoint returns { sid, raw_url, questions[{q_idx, t_start,
// t_end, snapshots, transcript, video_url, submit_url, score, feedback,
// gaps, ...}] }. Each snapshot now carries an explicit `kind` field from
// Gemini ("struggle" | "insight" | "procedural" | "blank"); the Reel
// respects it instead of re-classifying via regex on the description.
function _normalizeV2(d) {
  const qs = (d.questions || []).map((q) => {
    const snaps = (q.snapshots || []).filter((s) => s.kind !== 'blank');
    const tran = q.transcript || [];
    const frames = snaps.map((s) => ({
      filename: (s.url || '').split('/').pop() || '',
      url: s.url,
      timestamp: s.t,
    }));
    const markers = [
      ...snaps.map((s) => ({
        time: s.t,
        text: s.desc || '',
        description: s.desc || '',
        // Trust Gemini's kind if present; fall back to keyword classifier.
        kind: s.kind && s.kind !== 'procedural'
          ? s.kind
          : classifyEntry({ description: s.desc || '' }),
      })),
      ...tran.map((t) => ({
        time: t.t_start,
        text: t.text || '',
        kind: classifyEntry({ text: t.text || '' }),
      })),
      // Synthesize a submit marker per question so the scrubber always has
      // a visible "submit" anchor at t_end. Without this, a session with
      // no non-blank snapshots has an empty Greatest Hits tab.
      ...(q.submit_url ? [{
        time: Math.max(0, q.t_end - 0.05),
        text: `Submitted ${q.title || ('Q' + (q.q_idx + 1))}`,
        kind: 'submit',
      }] : []),
    ].sort((a, b) => a.time - b.time);
    return {
      idx: q.q_idx,
      title: q.title,
      start_seconds: q.t_start,
      end_seconds: q.t_end,
      frames,
      markers,
      audio_urls: [],
      submit_url: q.submit_url,
      video_url: q.video_url,
      score: q.score,
      feedback: q.feedback,
    };
  });
  return {
    session_id: d.sid,
    duration: d.duration,
    status: d.status,
    raw_url: d.raw_url,
    video: null,                                    // legacy top-level unused in v2 rendering
    timeline: qs.flatMap((q) => q.frames.map((f) => ({
      timestamp_seconds: f.timestamp,
      frame: f.filename,
      description: '',
      kind: 'neutral',
    }))),
    transcript: qs.flatMap((q, i) => ((d.questions[i].transcript || []).map((t) => ({
      timestamp_seconds: t.t_start,
      text: t.text || '',
    })))),
    frames: qs.flatMap((q) => q.frames),
    questions: qs,
  };
}

// ─── Pure helper: classify a replay entry into a marker kind ─────────────────
function classifyEntry(entry) {
  const text = (entry.text || entry.description || '').toLowerCase();
  const pause = entry.pause_seconds || 0;
  const score = entry.score != null ? entry.score : null;
  const isCorrect = entry.is_correct;
  const hasCorrections = entry.has_corrections;
  const type = entry.type || '';

  if (type === 'submit' || text.includes('submit') || text.includes('done ·') || text.includes('submitted')) {
    return 'submit';
  }
  if (
    pause >= 8 ||
    hasCorrections ||
    (score != null && score < 0.45) ||
    /\b(stall|pause|erase|cross.?out|stuck|blank|no response|couldn't|confused|wrong|error|hedge|basically|kind of|i think|not sure)\b/.test(text)
  ) {
    return 'struggle';
  }
  if (
    (score != null && score >= 0.75) ||
    isCorrect === true ||
    /\b(crisp|correct|clean|recovered|breakthrough|✓|right|nailed|understood|got it|derived|proved)\b/.test(text)
  ) {
    return 'insight';
  }
  return 'neutral';
}

// ─── Marker config ─────────────────────────────────────────────────────────
const MARKER = {
  struggle: { color: 'var(--tint-red)',   label: '??', title: 'Struggle' },
  insight:  { color: 'var(--ok)',         label: '!',  title: 'Insight'  },
  submit:   { color: 'var(--probe)',      label: '→',  title: 'Submit'   },
  neutral:  { color: 'var(--ink-3)',      label: '·',  title: 'Neutral'  },
};

// ─── Utility ───────────────────────────────────────────────────────────────
function fmtSec(s) {
  s = Math.round(s || 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function fmtDate(sessionId) {
  // sessionId looks like 20260419_143012 or similar
  if (!sessionId || sessionId.length < 8) return sessionId || '';
  const y = sessionId.slice(0, 4);
  const m = sessionId.slice(4, 6);
  const d = sessionId.slice(6, 8);
  return `${y}-${m}-${d}`;
}

// ─── Sub-components ────────────────────────────────────────────────────────

function EmptyState({ onBack }) {
  return (
    <div className="reel-empty-state">
      <div className="reel-empty-icon">
        <svg viewBox="0 0 48 48" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="6" y="10" width="36" height="28" rx="4"/>
          <circle cx="24" cy="24" r="6"/>
          <path d="M22 24l2.5 2.5L28 20"/>
        </svg>
      </div>
      <div className="reel-empty-title">No sessions yet</div>
      <div className="reel-empty-body">Start a PSET to see your replay here.</div>
      <button className="btn-primary" onClick={onBack}>← Back to Study</button>
    </div>
  );
}

// ─── Compact session header with change-session dropdown ───────────────────
function SessionHeader({ session, sessions, onChangeSession }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const dateStr = fmtDate(session.id);
  const durStr = session.duration > 0 ? fmtSec(session.duration) : '';
  const label = session.name
    ? session.name
    : (durStr ? `${dateStr} · ${durStr}` : dateStr);

  const older = sessions.filter(s => s.id !== session.id);

  return (
    <div className="reel-header-wrap" ref={wrapRef}>
      <div className="reel-header">
        <div className="reel-header-title">Replay · {label}</div>
        {older.length > 0 && (
          <button
            className="reel-change-session-btn"
            onClick={() => setOpen(v => !v)}
          >
            change session {open ? '▲' : '▼'}
          </button>
        )}
      </div>
      {open && (
        <div className="reel-session-dropdown">
          {older.map(s => {
            const d = fmtDate(s.id);
            const dur = s.duration > 0 ? fmtSec(s.duration) : '';
            const nm = s.name || (dur ? `${d} · ${dur}` : d);
            return (
              <div
                key={s.id}
                className="reel-session-dropdown-item"
                onClick={() => { onChangeSession(s.id); setOpen(false); }}
              >
                <span>{nm}</span>
                {dur && <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>{dur}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Question tabs ─────────────────────────────────────────────────────────
function QuestionTabs({ questions, activeTab, onTabChange }) {
  if (!questions || questions.length === 0) return null;

  // A tab is enabled if it has any observable content: a per-question video
  // clip, a submit screenshot, snapshot frames, or markers. Short sessions
  // produce no snapshots for later questions (the 15s ffmpeg interval hits
  // Q1 only), but that doesn't mean Q2/Q3 are unclickable — the submit
  // image still exists.
  const hasContent = (q) => !!(
    q.video_url ||
    q.submit_url ||
    (q.frames && q.frames.length > 0) ||
    (q.markers && q.markers.length > 0)
  );

  const tabs = questions.map(q => ({
    key: String(q.idx),
    label: q.idx === -1 ? 'Session' : `Q${q.idx + 1}`,
    hasFrames: hasContent(q),
  }));

  // Greatest Hits: enabled if any question has non-neutral markers
  const hasHighlights = questions.some(
    q => q.markers && q.markers.some(m => m.kind !== 'neutral')
  );
  tabs.push({ key: 'greatest_hits', label: 'Greatest Hits', hasFrames: hasHighlights });

  return (
    <div className="reel-question-tabs">
      {tabs.map(tab => (
        <button
          key={tab.key}
          className={'reel-question-tab' + (activeTab === tab.key ? ' is-on' : '')}
          disabled={!tab.hasFrames}
          onClick={() => onTabChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ─── Socratic tutor chat ───────────────────────────────────────────────────
// Streaming chat under the video + timeline. Each turn posts the full
// message history to /api/session/{sid}/chat along with `q_idx` and
// `frame_t` (the absolute playhead time) so the server pulls in the
// snapshot the user is literally looking at. Response streams back as
// chunked text/plain; we append chunks to the active assistant message
// as they arrive.
function TutorChat({ sid, qIdx, absTime, questionTitle }) {
  const [messages, setMessages] = React.useState([]);   // [{role, content}]
  const [input, setInput] = React.useState('');
  const [streaming, setStreaming] = React.useState(false);
  const [err, setErr] = React.useState('');
  const listRef = React.useRef(null);

  // Reset chat when question changes (fresh context).
  React.useEffect(() => {
    setMessages([]); setInput(''); setStreaming(false); setErr('');
  }, [sid, qIdx]);

  React.useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  async function send(e) {
    if (e) e.preventDefault();
    const text = input.trim();
    if (!text || streaming || !sid || qIdx === null || qIdx === undefined) return;
    setErr('');
    const nextHistory = [...messages, { role: 'user', content: text }];
    setMessages([...nextHistory, { role: 'assistant', content: '' }]);
    setInput('');
    setStreaming(true);

    try {
      const res = await fetch(`/api/session/${sid}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          q_idx: qIdx,
          frame_t: absTime,
          messages: nextHistory,
        }),
      });
      if (!res.ok || !res.body) {
        throw new Error(`chat ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let accum = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        accum += decoder.decode(value, { stream: true });
        setMessages((cur) => {
          const last = cur[cur.length - 1];
          if (!last || last.role !== 'assistant') return cur;
          const next = cur.slice(0, -1).concat({ role: 'assistant', content: accum });
          return next;
        });
      }
    } catch (e) {
      setErr(String(e.message || e));
      setMessages((cur) => {
        const last = cur[cur.length - 1];
        if (last && last.role === 'assistant' && !last.content) {
          return cur.slice(0, -1);
        }
        return cur;
      });
    } finally {
      setStreaming(false);
    }
  }

  return (
    <div className="tutor-chat">
      <div className="tutor-chat-header">
        <div className="tutor-chat-title">💬 Ask the tutor about this moment</div>
        <div className="tutor-chat-sub">
          {questionTitle ? questionTitle : 'this question'}
          {typeof absTime === 'number' && ` · at ${fmtSec(absTime)}`}
        </div>
      </div>
      <div className="tutor-chat-list" ref={listRef}>
        {messages.length === 0 && (
          <div className="tutor-chat-empty">
            The tutor sees the exact frame you're on right now, what you
            submitted, and what you said while solving. It won't give you
            the answer — it'll point at what you wrote, ask you the
            question that exposes the misconception, and let you climb
            out yourself.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={'tutor-chat-msg tutor-chat-msg--' + m.role}>
            <div className="tutor-chat-msg-role">
              {m.role === 'user' ? 'You' : 'Tutor'}
            </div>
            <div className="tutor-chat-msg-body">
              {m.content || (m.role === 'assistant' && streaming ? '…' : '')}
            </div>
          </div>
        ))}
      </div>
      {err && <div className="tutor-chat-err">{err}</div>}
      <form className="tutor-chat-input-row" onSubmit={send}>
        <input
          className="tutor-chat-input"
          placeholder="What do you think the next step is? Or paste your question…"
          value={input}
          onChange={e => setInput(e.target.value)}
          disabled={streaming}
        />
        <button
          className="btn-primary tutor-chat-btn"
          type="submit"
          disabled={streaming || !input.trim()}
        >
          {streaming ? 'Thinking…' : 'Ask'}
        </button>
      </form>
    </div>
  );
}


// ─── AI feedback card ──────────────────────────────────────────────────────
// Renders one question's Gemini-scored feedback at the top of the side
// panel. score may be null when scoring failed or is still pending; in
// that case feedback often carries the error message ("Scoring error:
// 503 UNAVAILABLE…") so the user sees WHY there's no analysis.
function FeedbackCard({ title, score, feedback, gaps }) {
  const hasScore = score !== null && score !== undefined;
  const pct = hasScore ? Math.round(score * 100) : null;
  const scoreClass = hasScore
    ? (pct >= 75 ? 'feedback-card--good' : pct >= 45 ? 'feedback-card--ok' : 'feedback-card--low')
    : 'feedback-card--pending';
  const isError = !hasScore && typeof feedback === 'string'
    && /error|failed|unavailable|timeout|retries/i.test(feedback);
  return (
    <div className={'feedback-card ' + scoreClass}>
      <div className="feedback-card-header">
        <div className="feedback-card-eyebrow">AI Review · {title || ''}</div>
        {hasScore && (
          <div className="feedback-card-score">{pct}%</div>
        )}
        {!hasScore && !isError && (
          <div className="feedback-card-pending">Scoring…</div>
        )}
        {isError && (
          <div className="feedback-card-error-badge">Error</div>
        )}
      </div>
      {feedback && (
        <div className="feedback-card-body">{feedback}</div>
      )}
      {Array.isArray(gaps) && gaps.length > 0 && (
        <ul className="feedback-card-gaps">
          {gaps.map((g, i) => <li key={i}>{g}</li>)}
        </ul>
      )}
      {!feedback && !hasScore && (
        <div className="feedback-card-body" style={{ opacity: 0.6 }}>
          No analysis yet. Check back in a minute; if it stays empty,
          Gemini hit a rate limit on this question.
        </div>
      )}
    </div>
  );
}

// ─── Viewer ────────────────────────────────────────────────────────────────
// v2 contract: every question has its own standalone clip with its own EBML
// header. currentTime is relative to the clip's start (0..segmentDuration).
// No clamping needed — the clip IS the segment.
function Viewer({ frames, currentTime, segmentDuration, segmentStart, videoUrl, playing, onTimeUpdate, onEnded }) {
  const absTime = segmentStart + currentTime;
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoUrl) return;
    if (Math.abs((v.currentTime || 0) - currentTime) > 0.25) {
      try { v.currentTime = currentTime; } catch (_) {}
    }
  }, [currentTime, videoUrl]);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoUrl) return;
    if (playing) { v.play().catch(() => {}); }
    else { v.pause(); }
  }, [playing, videoUrl]);

  if (videoUrl) {
    return (
      <div className="reel-viewer-inner" style={{ minHeight: 420, background: '#000' }}>
        <div className="reel-viewer-time-badge">{fmtSec(currentTime)} / {fmtSec(segmentDuration)}</div>
        <video
          key={videoUrl}
          ref={videoRef}
          src={videoUrl}
          className="reel-frame-img"
          style={{ minHeight: 420, width: '100%', objectFit: 'contain', background: '#fff' }}
          playsInline
          onTimeUpdate={(e) => onTimeUpdate && onTimeUpdate(e.target.currentTime)}
          onEnded={() => onEnded && onEnded()}
          preload="auto"
        />
      </div>
    );
  }

  const bestFrame = React.useMemo(() => {
    if (!frames || !frames.length) return null;
    let best = 0;
    for (let i = 0; i < frames.length; i++) {
      if ((frames[i].timestamp || 0) <= absTime) best = i;
      else break;
    }
    return frames[best];
  }, [frames, absTime]);

  if (bestFrame) {
    return (
      <div className="reel-viewer-inner" style={{ minHeight: 420, background: '#fefaf3' }}>
        <div className="reel-viewer-time-badge">{fmtSec(currentTime)} / {fmtSec(segmentDuration)}</div>
        <img
          src={bestFrame.url}
          alt={`Frame at ${fmtSec(absTime)}`}
          className="reel-frame-img"
          style={{ minHeight: 420, objectFit: 'contain' }}
        />
      </div>
    );
  }

  return (
    <div className="reel-viewer-inner reel-viewer-empty" style={{ minHeight: 420, background: '#fefaf3' }}>
      <div style={{ color: 'var(--ink-3)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>
        No frames or video recorded for this segment.
      </div>
    </div>
  );
}

// ─── Scrubber ──────────────────────────────────────────────────────────────
function ScrubberRow({ duration, currentTime, markers, onSeek, onPlay, onPause, playing, onPrevMarker, onNextMarker }) {
  const trackRef = React.useRef(null);

  function handleTrackClick(e) {
    if (!duration) return;
    const rect = trackRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(frac * duration);
  }

  const headPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="reel-scrubber-row">
      {/* Prev marker */}
      <button className="btn-ghost reel-nav-btn" onClick={onPrevMarker} title="Previous key moment">
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M10 3L5 8l5 5"/>
        </svg>
      </button>

      {/* Play / Pause */}
      <button className="reel-play-btn" onClick={playing ? onPause : onPlay} aria-label={playing ? 'Pause' : 'Play'}>
        {playing
          ? <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor"><rect x="3" y="2" width="3.5" height="12" rx="1"/><rect x="9.5" y="2" width="3.5" height="12" rx="1"/></svg>
          : <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor"><path d="M4 2l10 6-10 6z"/></svg>
        }
      </button>

      {/* Next marker */}
      <button className="btn-ghost reel-nav-btn" onClick={onNextMarker} title="Next key moment">
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M6 3l5 5-5 5"/>
        </svg>
      </button>

      {/* Track */}
      <div className="reel-track" ref={trackRef} onClick={handleTrackClick}>
        <div className="reel-track-fill" style={{ width: headPct + '%' }} />
        {markers.map((m, i) => {
          if (!duration) return null;
          // markers carry relative time (within segment)
          const pct = (m.relTime / duration) * 100;
          const cfg = MARKER[m.kind] || MARKER.neutral;
          return (
            <button
              key={i}
              className="reel-marker"
              style={{ left: pct + '%', background: cfg.color }}
              onClick={e => { e.stopPropagation(); onSeek(m.relTime); }}
              title={`${cfg.title} — ${fmtSec(m.relTime)}`}
            />
          );
        })}
        <div className="reel-playhead" style={{ left: headPct + '%' }} />
      </div>

      {/* Time label */}
      <div className="reel-time-label">{fmtSec(currentTime)} / {fmtSec(duration)}</div>

      {/* Jump buttons */}
      <button className="btn-ghost reel-jump-btn reel-jump-struggle" onClick={() => {
        const prev = [...markers].reverse().find(m => m.kind === 'struggle' && m.relTime < currentTime - 1);
        if (prev) onSeek(prev.relTime);
        else {
          const first = markers.find(m => m.kind === 'struggle');
          if (first) onSeek(first.relTime);
        }
      }}>
        ← Struggle
      </button>
      <button className="btn-ghost reel-jump-btn reel-jump-insight" onClick={() => {
        const next = markers.find(m => m.kind === 'insight' && m.relTime > currentTime + 1);
        if (next) onSeek(next.relTime);
        else {
          const last = [...markers].reverse().find(m => m.kind === 'insight');
          if (last) onSeek(last.relTime);
        }
      }}>
        Insight →
      </button>
    </div>
  );
}

// ─── Transcript panel ──────────────────────────────────────────────────────
// Renders the active question's transcript entries as timestamped prose.
// Clicking an entry seeks the player to its t_start. The active entry
// (nearest-before currentTime) highlights and auto-scrolls into view.
function TranscriptPanel({ entries, currentTime, segmentStart, onSeek }) {
  const listRef = React.useRef(null);
  React.useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector('.transcript-entry.active');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentTime]);

  if (!entries || entries.length === 0) {
    return (
      <div className="transcript-empty">
        No transcript for this segment. Mic may have been off, or Gemini
        audio transcription hasn't finished yet.
      </div>
    );
  }

  // entries are absolute-time transcript entries within this segment's
  // window. currentTime is segment-relative; compare via segmentStart.
  const absTime = segmentStart + currentTime;
  let activeIdx = -1, bestDist = Infinity;
  entries.forEach((e, i) => {
    const d = absTime - (e.t_start || e.timestamp_seconds || 0);
    if (d >= 0 && d < bestDist) { bestDist = d; activeIdx = i; }
  });

  return (
    <div className="transcript-panel" ref={listRef}>
      {entries.map((e, i) => {
        const tStart = e.t_start ?? e.timestamp_seconds ?? 0;
        return (
          <div
            key={i}
            className={'transcript-entry' + (i === activeIdx ? ' active' : '')}
            onClick={() => onSeek(Math.max(0, tStart - segmentStart))}
          >
            <span className="transcript-time">{fmtSec(tStart)}</span>
            <span className="transcript-text">{e.text}</span>
          </div>
        );
      })}
    </div>
  );
}


// ─── Search bar ────────────────────────────────────────────────────────────
// Natural-language query → /api/search → show ranked results, click to
// jump to that session + time. Exposes the semantic retrieval the local
// pipeline always had but the Reel never surfaced.
function SearchBar({ onJump }) {
  const [q, setQ] = React.useState('');
  const [results, setResults] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');

  async function run(e) {
    if (e) e.preventDefault();
    if (!q.trim()) { setResults(null); return; }
    setLoading(true); setErr('');
    try {
      const r = await fetch('/api/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q.trim(), limit: 6 }),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setResults(data.results || []);
    } catch (e) {
      setErr(String(e.message || e));
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="reel-search">
      <form className="reel-search-row" onSubmit={run}>
        <input
          className="reel-search-input"
          placeholder="Search your sessions — e.g. 'where I got stuck on kinematics'"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <button className="btn-ghost reel-search-btn" type="submit" disabled={loading || !q.trim()}>
          {loading ? 'Searching…' : 'Search'}
        </button>
        {results && (
          <button className="btn-ghost reel-search-btn" type="button"
                  onClick={() => { setResults(null); setQ(''); }}>
            Clear
          </button>
        )}
      </form>
      {err && <div className="reel-search-err">{err}</div>}
      {results && results.length === 0 && !loading && !err && (
        <div className="reel-search-empty">No matches.</div>
      )}
      {results && results.length > 0 && (
        <div className="reel-search-results">
          {results.map((r, i) => (
            <div
              key={i}
              className="reel-search-hit"
              onClick={() => onJump && onJump(r.session, r.start_time || 0)}
            >
              <div className="reel-search-hit-meta">
                {fmtDate(r.session)} · {fmtSec(r.start_time || 0)} ·
                sim {(r.similarity || 0).toFixed(2)}
              </div>
              {r.snapshots && r.snapshots[0] && (
                <div className="reel-search-hit-desc">
                  {r.snapshots[0].description || r.snapshots[0].desc || ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Marker right panel ────────────────────────────────────────────────────
function MarkerPanel({ markers, currentTime, onSeek }) {
  const listRef = React.useRef(null);

  React.useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('.reel-entry.active');
    if (active) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentTime]);

  const activeIdx = React.useMemo(() => {
    let best = -1, bestDist = Infinity;
    markers.forEach((m, i) => {
      const dist = currentTime - m.relTime;
      if (dist >= 0 && dist < bestDist) { bestDist = dist; best = i; }
    });
    return best;
  }, [markers, currentTime]);

  if (!markers.length) {
    return <div className="reel-entry-empty">No key moments in this segment.</div>;
  }

  return (
    <div className="reel-entry-list" ref={listRef} style={{ maxHeight: 480 }}>
      {markers.map((m, i) => {
        const cfg = MARKER[m.kind] || MARKER.neutral;
        return (
          <div
            key={i}
            className={'reel-entry' + (i === activeIdx ? ' active' : '')}
            onClick={() => onSeek(m.relTime)}
          >
            <div className="reel-entry-left">
              <div className="reel-entry-dot" style={{ background: cfg.color }} title={cfg.title} />
              <span className="reel-entry-time">{fmtSec(m.relTime)}</span>
            </div>
            <div className="reel-entry-text">{m.text || cfg.title}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Audio player (best-effort sequential chunk playback) ──────────────────
function AudioPlayer({ audioUrls, playing, currentTime, audioRef }) {
  // Keep a local ref to avoid repeated effect runs on each render
  const urlsRef = React.useRef(audioUrls);
  urlsRef.current = audioUrls;

  // When the audio URLs change (new segment), reset src
  React.useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (!audioUrls || !audioUrls.length) { el.src = ''; return; }
    // Pick the chunk that most likely contains currentTime.
    // Chunks named like 0_30.webm — parse start from filename.
    function chunkStart(url) {
      const m = url.match(/(\d+)_\d+\.webm$/);
      return m ? parseInt(m[1], 10) : 0;
    }
    // Find the chunk whose start <= currentTime and is closest
    const sorted = [...audioUrls].sort((a, b) => chunkStart(a) - chunkStart(b));
    let best = sorted[0];
    for (const u of sorted) {
      if (chunkStart(u) <= currentTime) best = u;
      else break;
    }
    if (el.src !== (window.location.origin + best)) {
      el.src = best;
      el.load();
    }
  }, [audioUrls]);

  // Play/pause sync
  React.useEffect(() => {
    const el = audioRef.current;
    if (!el || !audioUrls || !audioUrls.length) return;
    if (playing) {
      el.play().catch(() => {}); // ignore autoplay block
    } else {
      el.pause();
    }
  }, [playing]);

  if (!audioUrls || !audioUrls.length) return null;
  return <audio ref={audioRef} style={{ display: 'none' }} preload="auto" />;
}

// ─── CloseTheLoopCard ──────────────────────────────────────────────────────
function CloseTheLoopCard({ marker, sessionId }) {
  const [phase, setPhase] = React.useState('idle');
  const [questions, setQuestions] = React.useState([]);
  const [answers, setAnswers] = React.useState({});
  const [error, setError] = React.useState('');
  const [updatedIds, setUpdatedIds] = React.useState([]);

  function fetchQuestions() {
    setPhase('loading');
    setError('');
    fetch('/api/struggle/followup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        timestamp_seconds: marker.absTime,
        marker_text: marker.text || '',
      }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); setPhase('idle'); return; }
        setQuestions(data.questions || []);
        setAnswers({});
        setPhase('questions');
      })
      .catch(e => { setError(String(e)); setPhase('idle'); });
  }

  function submitAnswers() {
    const hasAny = questions.some(q => (answers[q.id] || '').trim().length > 0);
    if (!hasAny) { setError('Write at least one answer before submitting.'); return; }

    const resolvePromises = questions.map(q =>
      fetch('/api/struggle/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: q.id,
          concept_ids: q.expected_concepts || [],
          correct: (answers[q.id] || '').trim().length > 0,
          evidence_text: answers[q.id] || '',
        }),
      }).then(r => r.json())
    );

    Promise.all(resolvePromises).then(results => {
      const ids = [...new Set(results.flatMap(r => r.updated || []))];
      setUpdatedIds(ids);
      setPhase('submitted');
      if (ids.length > 0) {
        window.__didacticProvenQueue = window.__didacticProvenQueue || [];
        window.__didacticProvenQueue.push(...ids);
        window.dispatchEvent(new CustomEvent('didactic:conceptProven', { detail: { ids } }));
      }
    }).catch(e => setError(String(e)));
  }

  if (phase === 'idle') {
    return (
      <div className="struggle-loop-card">
        <div className="struggle-loop-title">Close the loop</div>
        <div className="struggle-loop-body">Review what tripped you up — answer a quick follow-up to mark the concept as resolved.</div>
        <button className="btn-primary struggle-loop-btn" onClick={fetchQuestions}>
          Get follow-up questions
        </button>
        {error && <div className="struggle-loop-error">{error}</div>}
      </div>
    );
  }
  if (phase === 'loading') {
    return (
      <div className="struggle-loop-card">
        <div className="struggle-loop-title">Generating questions…</div>
        <div className="struggle-loop-spinner" />
      </div>
    );
  }
  if (phase === 'submitted') {
    return (
      <div className="struggle-loop-card struggle-loop-success">
        <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="var(--ok)" strokeWidth="1.8">
          <circle cx="10" cy="10" r="8"/>
          <path d="M6.5 10.5l2.5 2.5 4.5-5"/>
        </svg>
        <div className="struggle-loop-title" style={{ color: 'var(--ok)' }}>Resolved!</div>
        {updatedIds.length > 0 && (
          <div className="struggle-loop-body">{updatedIds.length} concept{updatedIds.length > 1 ? 's' : ''} marked resolved. Check your graph.</div>
        )}
      </div>
    );
  }
  // phase === 'questions'
  return (
    <div className="struggle-loop-card">
      <div className="struggle-loop-title">Close the loop</div>
      <div className="struggle-loop-qs">
        {questions.map((q, i) => (
          <div key={q.id} className="struggle-loop-q">
            <div className="struggle-loop-q-prompt">{i + 1}. {q.prompt}</div>
            <textarea
              className="struggle-loop-q-input"
              placeholder="Write your answer…"
              value={answers[q.id] || ''}
              rows={3}
              onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      {error && <div className="struggle-loop-error">{error}</div>}
      <button className="btn-primary struggle-loop-btn" onClick={submitAnswers}>
        Submit answers
      </button>
    </div>
  );
}

// ─── Main screen ───────────────────────────────────────────────────────────

export function StruggleReelScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const onBack = () => navigate('/');
  const [sessions, setSessions] = React.useState(null);   // null = loading
  const [selectedId, setSelectedId] = React.useState('');
  const [replayData, setReplayData] = React.useState(null);
  const [replayError, setReplayError] = React.useState('');
  const [loadingReplay, setLoadingReplay] = React.useState(false);

  // Playback state (always relative to active segment, 0-based)
  const [currentTime, setCurrentTime] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);

  // Active question tab key
  const [activeTab, setActiveTab] = React.useState(null);

  // Close-the-loop
  const [activeStruggle, setActiveStruggle] = React.useState(null);

  const timerRef = React.useRef(null);
  const audioRef = React.useRef(null);

  // ── Fetch session list on mount; prefer URL param over newest ────────────
  React.useEffect(() => {
    fetch('/api/sessions')
      .then(r => r.json())
      .then(data => {
        // Show every session that has ANY content. v2 short sessions may
        // have zero derived snapshots (ffmpeg fps=1/15 doesn't fire for
        // <15s videos) but still have a video and submits — drop the old
        // frame_count gate.
        const valid = (data.sessions || [])
          .filter(s => s.has_video || s.frame_count > 0)
          .sort((a, b) => b.id.localeCompare(a.id));
        setSessions(valid);
        if (params.sessionId) setSelectedId(params.sessionId);
        else if (valid.length > 0) setSelectedId(valid[0].id);
      })
      .catch(() => setSessions([]));
  }, [params.sessionId]);

  // ── Fetch replay when session changes ────────────────────────────────────
  React.useEffect(() => {
    if (!selectedId) { setReplayData(null); setReplayError(''); return; }
    setLoadingReplay(true);
    setReplayData(null);
    setReplayError('');
    setCurrentTime(0);
    setPlaying(false);
    setActiveTab(null);
    clearInterval(timerRef.current);

    // v2 only — GET /api/session/{sid}/replay. Legacy plural endpoint
    // and its adapter were deleted in the v2 rollout (plan step 6);
    // every session on disk is migrated to schema_version=2.
    (async () => {
      let data = null;
      try {
        const r = await fetch(`/api/session/${selectedId}/replay`);
        if (!r.ok) throw new Error(r.status === 404 ? 'not_found' : `replay ${r.status}`);
        data = _normalizeV2(await r.json());
      } catch (err) {
        setReplayError(err.message === 'not_found'
          ? 'This session has no recorded frames.'
          : 'Failed to load replay.');
        setLoadingReplay(false);
        return;
      }
      setReplayData(data);
      setLoadingReplay(false);
      const qs = data.questions || [];
      const hasHighlights = qs.some(q =>
        q.markers && q.markers.some(m => m.kind !== 'neutral')
      );
      if (hasHighlights) setActiveTab('greatest_hits');
      else if (qs.length > 0) setActiveTab(String(qs[0].idx));
    })();
  }, [selectedId]);

  // ── Derive active segment from tab selection ──────────────────────────────
  const activeSegment = React.useMemo(() => {
    if (!replayData) return null;
    const qs = replayData.questions || [];
    if (!qs.length) return null;

    if (activeTab === 'greatest_hits') {
      // Greatest Hits: collect all non-neutral markers across all questions,
      // expose the whole session's frames, time range covers full duration.
      const allMarkers = qs.flatMap(q =>
        (q.markers || []).filter(m => m.kind !== 'neutral').map(m => ({
          ...m,
          relTime: m.time - (q.start_seconds || 0),
          absTime: m.time,
        }))
      ).sort((a, b) => a.absTime - b.absTime);

      // Renumber relTime to be evenly spaced for a condensed "reel"
      // Actually: keep absolute positions but collapse to session-relative.
      const sessionStart = qs[0].start_seconds || 0;
      const sessionEnd = qs[qs.length - 1].end_seconds || replayData.duration || 0;
      const allFrames = replayData.frames || [];
      return {
        start_seconds: sessionStart,
        end_seconds: sessionEnd,
        frames: allFrames,
        markers: allMarkers.map(m => ({ ...m, relTime: m.absTime - sessionStart })),
        audio_urls: qs.flatMap(q => q.audio_urls || []),
        duration: sessionEnd - sessionStart,
      };
    }

    const q = qs.find(q => String(q.idx) === activeTab);
    if (!q) return null;

    // Build relative markers (time within this segment, 0-based)
    const relMarkers = (q.markers || []).map(m => ({
      ...m,
      relTime: m.time - (q.start_seconds || 0),
      absTime: m.time,
    }));

    return {
      idx: q.idx,
      title: q.title,
      start_seconds: q.start_seconds || 0,
      end_seconds: q.end_seconds || replayData.duration || 0,
      frames: q.frames || replayData.frames || [],
      markers: relMarkers,
      audio_urls: q.audio_urls || [],
      video_url: q.video_url || null,
      duration: Math.max(0.1, (q.end_seconds || 0) - (q.start_seconds || 0)),
      // Per-question AI analysis — populated from scores/q{nn}.json on the
      // server via /replay → _normalizeV2. `score` is null when scoring
      // failed or is still pending; `feedback` may contain an error string
      // (e.g. "Scoring error: 503 UNAVAILABLE") so the user sees WHY there's
      // no analysis rather than a silent empty card.
      score: q.score,
      feedback: q.feedback,
      gaps: q.gaps || [],
    };
  }, [replayData, activeTab]);

  // ── Play timer — only used when there's no real <video> driving time ─────
  // When the active segment has video_url, the <video>'s onTimeUpdate drives
  // setCurrentTime directly at native speed. Otherwise we fake a 5×-speed
  // scrubber over the frame sequence.
  React.useEffect(() => {
    clearInterval(timerRef.current);
    if (!playing || !activeSegment) return;
    if (activeSegment.video_url) return;  // native <video> owns playback
    const segDur = activeSegment.duration;
    timerRef.current = setInterval(() => {
      setCurrentTime(prev => {
        if (prev >= segDur) { setPlaying(false); return segDur; }
        return prev + 0.2;
      });
    }, 100);
    return () => clearInterval(timerRef.current);
  }, [playing, activeSegment]);

  // ── Reset time when tab changes ───────────────────────────────────────────
  React.useEffect(() => {
    setCurrentTime(0);
    setPlaying(false);
    clearInterval(timerRef.current);
  }, [activeTab]);

  // ── Close-the-loop: detect nearby struggle marker ──────────────────────────
  const segMarkers = activeSegment ? activeSegment.markers : [];
  React.useEffect(() => {
    const nearby = segMarkers.find(m => m.kind === 'struggle' && Math.abs(m.relTime - currentTime) < 3);
    setActiveStruggle(nearby || null);
  }, [currentTime, segMarkers]);

  // ── Prev / next marker navigation (relative times) ────────────────────────
  function goPrevMarker() {
    const prev = [...segMarkers].reverse().find(m => m.relTime < currentTime - 1);
    if (prev) setCurrentTime(prev.relTime);
    else if (segMarkers.length) setCurrentTime(segMarkers[0].relTime);
  }
  function goNextMarker() {
    const next = segMarkers.find(m => m.relTime > currentTime + 1);
    if (next) setCurrentTime(next.relTime);
    else if (segMarkers.length) setCurrentTime(segMarkers[segMarkers.length - 1].relTime);
  }

  // ── Loading state ──────────────────────────────────────────────────────────
  if (sessions === null) {
    return (
      <section className="reel">
        <div className="reel-loading-msg">Loading sessions…</div>
      </section>
    );
  }

  if (sessions.length === 0) {
    return (
      <section className="reel">
        <EmptyState onBack={onBack} />
      </section>
    );
  }

  const selectedSession = sessions.find(s => s.id === selectedId) || sessions[0];
  const qs = replayData ? (replayData.questions || []) : [];
  const segDur = activeSegment ? activeSegment.duration : 0;
  const segFrames = activeSegment ? activeSegment.frames : [];
  const segStart = activeSegment ? activeSegment.start_seconds : 0;
  const audioUrls = activeSegment ? activeSegment.audio_urls : [];

  return (
    <section className="reel">
      {/* Back + compact session header */}
      <div className="reel-top">
        <button className="back-btn" onClick={onBack}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 6l-6 6 6 6"/>
          </svg>
          Back
        </button>
        <div style={{ flex: 1 }}>
          <SessionHeader
            session={selectedSession}
            sessions={sessions}
            onChangeSession={id => { setSelectedId(id); }}
          />
        </div>
      </div>

      {/* Semantic search over every session — click a hit to jump to it. */}
      <SearchBar onJump={(sid) => setSelectedId(sid)} />

      {/* Question tabs */}
      {!loadingReplay && replayData && (
        <QuestionTabs
          questions={qs}
          activeTab={activeTab}
          onTabChange={tab => {
            // Reset currentTime + pause IN THE SAME EVENT as the tab
            // change so the Viewer's seek effect sees currentTime=0 on
            // its first pass with the new video_url. Without this, the
            // first click seeks the new clip to the OLD currentTime and
            // appears to do nothing until the second click catches up.
            setCurrentTime(0);
            setPlaying(false);
            setActiveTab(tab);
          }}
        />
      )}

      {/* Body: viewer left (70%) + marker panel right (30%) */}
      <div className="reel-body">
        <div className="reel-stage">
          {/* Viewer */}
          <div className="reel-viewport" style={{ aspectRatio: 'unset', minHeight: 420, background: '#fefaf3' }}>
            {loadingReplay && (
              <div className="reel-viewer-inner reel-viewer-empty" style={{ minHeight: 420 }}>
                <span style={{ color: 'var(--ink-3)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>Loading…</span>
              </div>
            )}
            {!loadingReplay && replayError && (
              <div className="reel-viewer-inner reel-viewer-empty" style={{ minHeight: 420 }}>
                <span style={{ color: 'var(--ink-3)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>{replayError}</span>
              </div>
            )}
            {!loadingReplay && !replayError && replayData && (
              <Viewer
                frames={segFrames}
                currentTime={currentTime}
                segmentDuration={segDur}
                segmentStart={segStart}
                videoUrl={activeSegment?.video_url
                  || (replayData.raw_url || (replayData.video && replayData.video.url) || null)}
                playing={playing}
                onTimeUpdate={(t) => setCurrentTime(Math.max(0, Math.min(segDur, t)))}
                onEnded={() => setPlaying(false)}
              />
            )}
          </div>

          {/* Scrubber */}
          {replayData && segDur > 0 && (
            <ScrubberRow
              duration={segDur}
              currentTime={currentTime}
              markers={segMarkers}
              playing={playing}
              onPlay={() => {
                // If already at the end of the segment, rewind before playing
                // so the clamp doesn't immediately pause again.
                if (currentTime >= segDur - 0.1) setCurrentTime(0);
                setPlaying(true);
              }}
              onPause={() => setPlaying(false)}
              onSeek={t => { setCurrentTime(Math.max(0, Math.min(segDur, t))); setPlaying(false); }}
              onPrevMarker={goPrevMarker}
              onNextMarker={goNextMarker}
            />
          )}

          {/* Close the loop — shown when parked on a struggle marker */}
          {activeStruggle && (
            <CloseTheLoopCard
              key={activeStruggle.absTime}
              marker={activeStruggle}
              sessionId={selectedId}
            />
          )}

          {/* Socratic tutor — lives UNDER the video and UNDER the timeline.
              Always rendered (even on Greatest Hits — falls back to q0's
              context then). Streams Gemini responses with the question
              meta, transcript, snapshots, feedback, submit image, AND the
              exact frame the user is reviewing (server picks nearest
              snapshot to absTime). Never gives answers — asks questions
              that expose the misconception in the student's own written
              work. */}
          {selectedId && replayData && (replayData.questions || []).length > 0 && (
            <TutorChat
              sid={selectedId}
              qIdx={
                activeSegment && typeof activeSegment.idx === 'number' && activeSegment.idx >= 0
                  ? activeSegment.idx
                  : (replayData.questions[0].idx ?? 0)
              }
              absTime={segStart + currentTime}
              questionTitle={
                (activeSegment && activeSegment.title)
                || replayData.questions[0].title
                || 'this session'
              }
            />
          )}
        </div>

        {/* Right: AI analysis + marker panel */}
        <aside className="reel-side">
          {/* Per-question AI analysis card. Shows the Gemini score +
              feedback when present, or the error ("Scoring error: 503
              UNAVAILABLE…") when scoring failed — so the user sees why
              there's no analysis instead of an unexplained empty panel. */}
          {activeSegment && activeTab !== 'greatest_hits' && (
            <FeedbackCard
              title={activeSegment.title}
              score={activeSegment.score}
              feedback={activeSegment.feedback}
              gaps={activeSegment.gaps}
            />
          )}
          <div className="reel-side-header eyebrow-sm">Key Moments</div>
          {segMarkers.length > 0 && (
            <div className="reel-legend">
              {['struggle', 'insight', 'submit'].map(k => (
                <div key={k} className="reel-legend-item">
                  <div className="reel-legend-dot" style={{ background: MARKER[k].color }} />
                  <span>{MARKER[k].title}</span>
                </div>
              ))}
            </div>
          )}
          <MarkerPanel
            markers={segMarkers}
            currentTime={currentTime}
            onSeek={t => { setCurrentTime(Math.max(0, Math.min(segDur, t))); setPlaying(false); }}
          />

          {/* Transcript panel — what the student said during this segment.
              Empty message surfaces WHY when mic was off or Gemini audio
              transcription hasn't finished. */}
          <div className="reel-side-header eyebrow-sm" style={{ marginTop: 14 }}>Transcript</div>
          <TranscriptPanel
            entries={(replayData?.transcript || []).filter(e => {
              const t = e.t_start ?? e.timestamp_seconds ?? 0;
              return t >= segStart && t <= segStart + segDur;
            })}
            currentTime={currentTime}
            segmentStart={segStart}
            onSeek={t => { setCurrentTime(Math.max(0, Math.min(segDur, t))); setPlaying(false); }}
          />
        </aside>
      </div>

      {/* Hidden audio element */}
      <AudioPlayer
        audioUrls={audioUrls}
        playing={playing}
        currentTime={currentTime + segStart}
        audioRef={audioRef}
      />
    </section>
  );
}

